import { useSyncExternalStore } from 'react';
import { type DetailedError, Upload } from 'tus-js-client';
import { queryClient } from './query';

/**
 * Gestore dei caricamenti (tus): i file viaggiano a pezzi da 8 MB e, se cade la rete o si
 * chiude l'app, riprendono da dove erano arrivati quando si risceglie lo stesso file.
 * Continua a funzionare mentre ci si sposta tra le pagine di Regia.
 */

export type UploadStatus = 'uploading' | 'error' | 'done' | 'canceled';

export interface UploadEntry {
  key: string;
  fileName: string;
  size: number;
  kind: 'image' | 'video' | 'file';
  pageId: string;
  pageName: string;
  status: UploadStatus;
  progress: number;
  error: string | null;
  mediaId: string | null;
  resumed: boolean;
}

const CHUNK = 8 * 1024 * 1024;
const MIME_BY_EXT: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  heic: 'image/heic',
  heif: 'image/heif',
  webp: 'image/webp',
  gif: 'image/gif',
  mp4: 'video/mp4',
  mov: 'video/quicktime',
  m4v: 'video/x-m4v',
  webm: 'video/webm',
  pdf: 'application/pdf',
};

export function fileType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return MIME_BY_EXT[ext] ?? 'application/octet-stream';
}

let entries: UploadEntry[] = [];
const uploads = new Map<string, { upload: Upload; file: File }>();
const listeners = new Set<() => void>();
let wakeLock: { release(): Promise<void> } | null = null;
let counter = 0;

function emit() {
  entries = [...entries];
  for (const l of listeners) l();
  void syncWakeLock();
}

function patch(key: string, change: Partial<UploadEntry>) {
  entries = entries.map((e) => (e.key === key ? { ...e, ...change } : e));
  emit();
}

const active = () => entries.some((e) => e.status === 'uploading');

/** Tiene acceso lo schermo del telefono mentre si carica (lo spegnimento metterebbe in pausa). */
async function syncWakeLock() {
  try {
    if (active() && !wakeLock && 'wakeLock' in navigator) {
      wakeLock = await (
        navigator as Navigator & {
          wakeLock: { request(t: 'screen'): Promise<{ release(): Promise<void> }> };
        }
      ).wakeLock.request('screen');
    } else if (!active() && wakeLock) {
      await wakeLock.release();
      wakeLock = null;
    }
  } catch {
    wakeLock = null;
  }
}

if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (e) => {
    if (active()) e.preventDefault();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      wakeLock = null;
      void syncWakeLock();
    }
  });
}

function messageFor(err: Error | DetailedError): string {
  const res = (err as DetailedError).originalResponse;
  const status = res?.getStatus();
  if (status === 401) return 'Sessione scaduta: accedi di nuovo e riscegli il file';
  if (status === 413) return 'File troppo grande (massimo 4 GB)';
  if (status === 415) return 'Tipo di file non ammesso: carica foto, video o PDF';
  if (status === 400) return res?.getBody() || 'Caricamento rifiutato';
  return 'Connessione interrotta. Riprova: riparte da dove era arrivato';
}

function begin(key: string) {
  const item = uploads.get(key);
  if (!item) return;
  void item.upload.findPreviousUploads().then((previous) => {
    const last = previous[0];
    if (last) {
      item.upload.resumeFromPreviousUpload(last);
      patch(key, { resumed: true });
    }
    item.upload.start();
  });
}

export function startUploads(files: File[], page: { id: string; name: string }): void {
  for (const file of files) {
    const key = `u${++counter}-${file.name}`;
    const type = fileType(file);
    const entry: UploadEntry = {
      key,
      fileName: file.name,
      size: file.size,
      kind: type.startsWith('image/') ? 'image' : type.startsWith('video/') ? 'video' : 'file',
      pageId: page.id,
      pageName: page.name,
      status: 'uploading',
      progress: 0,
      error: null,
      mediaId: null,
      resumed: false,
    };
    const upload = new Upload(file, {
      endpoint: '/api/media/uploads',
      chunkSize: CHUNK,
      retryDelays: [0, 2000, 5000, 10_000, 20_000, 30_000, 60_000],
      metadata: { filename: file.name, filetype: type, pageId: page.id },
      headers: { 'x-regia': '1' },
      storeFingerprintForResuming: true,
      removeFingerprintOnSuccess: true,
      onProgress: (sent, total) => patch(key, { progress: total ? sent / total : 0 }),
      onSuccess: ({ lastResponse }) => {
        patch(key, {
          status: 'done',
          progress: 1,
          mediaId: lastResponse.getHeader('Regia-Media-Id') ?? null,
        });
        queryClient.invalidateQueries({ queryKey: ['media'] });
        queryClient.invalidateQueries({ queryKey: ['summary'] });
      },
      onError: (err) => patch(key, { status: 'error', error: messageFor(err) }),
      onShouldRetry: (err) => {
        const status = err.originalResponse?.getStatus() ?? 0;
        return !(
          status >= 400 &&
          status < 500 &&
          status !== 409 &&
          status !== 423 &&
          status !== 429
        );
      },
    });
    uploads.set(key, { upload, file });
    entries = [entry, ...entries];
    emit();
    begin(key);
  }
}

export function retryUpload(key: string): void {
  patch(key, { status: 'uploading', error: null });
  begin(key);
}

export function cancelUpload(key: string): void {
  const item = uploads.get(key);
  void item?.upload.abort(true);
  patch(key, { status: 'canceled' });
}

export function clearFinished(): void {
  entries = entries.filter((e) => e.status === 'uploading' || e.status === 'error');
  for (const key of [...uploads.keys()])
    if (!entries.some((e) => e.key === key)) uploads.delete(key);
  emit();
}

export function useUploads(): UploadEntry[] {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => entries,
    () => entries,
  );
}
